import { useState } from "react";
import { getApiBaseUrl } from "../utils/api";

const API_BASE_URL = getApiBaseUrl();

const useAdminActions = (user, onCoursesChange, onDataRefresh) => {
  // Modal states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  // Loading state for actions
  const [loading, setLoading] = useState(false);

  // Course actions
  const handleCreateCourse = async (courseData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/courses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...courseData, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowCreateForm(false);
        onCoursesChange();
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        throw new Error(data.message || "Erro ao criar curso");
      }
    } catch (error) {
      console.error("Error creating course:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCourse = async (courseId, courseData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...courseData, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowCreateForm(false);
        setEditingCourse(null);
        onCoursesChange();
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        throw new Error(data.message || "Erro ao atualizar curso");
      }
    } catch (error) {
      console.error("Error updating course:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Tem certeza que deseja deletar este curso?")) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (response.ok) {
        onCoursesChange();
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        const errorData = await response.json();
        alert(errorData.detail || "Erro ao deletar curso");
      }
    } catch (error) {
      console.error("Error deleting course:", error);
      alert("Erro ao deletar curso");
    }
  };

  const handleDeactivateCourse = async (courseId) => {
    if (!window.confirm("Tem certeza que deseja inativar este curso? Ele não aparecerá mais para os usuários, mas os certificados emitidos serão preservados.")) return;

    try {
      const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}/deactivate`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (response.ok) {
        onCoursesChange();
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
        alert("Curso inativado com sucesso");
      } else {
        const errorData = await response.json();
        alert(errorData.detail || "Erro ao inativar curso");
      }
    } catch (error) {
      console.error("Error deactivating course:", error);
      alert("Erro ao inativar curso");
    }
  };

  const handleActivateCourse = async (courseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/courses/${courseId}/activate`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (response.ok) {
        onCoursesChange();
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
        alert("Curso ativado com sucesso");
      } else {
        const errorData = await response.json();
        alert(errorData.detail || "Erro ao ativar curso");
      }
    } catch (error) {
      console.error("Error activating course:", error);
      alert("Erro ao ativar curso");
    }
  };

  // User actions
  const handleCreateUser = async (userData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...userData, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowUserForm(false);
        setShowCreateForm(false);
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        throw new Error(data.message || "Erro ao criar usuário");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...userData, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowUserForm(false);
        setEditingUser(null);
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        throw new Error(data.message || "Erro ao atualizar usuário");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleViewUserDashboard = (user) => {
    // This will be handled by the component that uses this hook
    // The component should use navigate() instead of window.location.href
  };

  // Group actions
  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Tem certeza que deseja deletar este grupo?")) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/groups/${groupId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error("Error parsing JSON:", jsonError);
        data = { message: "Erro ao processar resposta do servidor" };
      }

      if (response.ok) {
        // Refresh groups list
        if (onDataRefresh) {
          onDataRefresh();
        }
        alert("Grupo deletado com sucesso");
      } else {
        // Check if it's a courses-related error
        if (data.detail && data.detail.includes("curso(s) associado(s)")) {
          // Extract course information from the error message
          const courseMatch = data.detail.match(/(\d+) curso\(s\) associado\(s\): (.+)/);
          if (courseMatch) {
            const courseCount = courseMatch[1];
            const courseList = courseMatch[2];
            const forceDelete = window.confirm(
              `Este grupo possui ${courseCount} curso(s) vinculado(s): ${courseList}\n\n` +
              `Deseja deletar o grupo mesmo assim? Os cursos serão tornados públicos.`
            );
            
            if (forceDelete) {
              await handleForceDeleteGroup(groupId);
            }
          } else {
            alert(data.detail || "Erro ao deletar grupo");
          }
        } else {
          // Show other error messages
          const errorMessage = data.detail || data.message || "Erro ao deletar grupo";
          alert(errorMessage);
        }
        console.error("Error deleting group:", data);
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      alert("Erro ao deletar grupo. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleForceDeleteGroup = async (groupId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/groups/${groupId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, force_delete: true }),
      });

      const data = await response.json();
      if (response.ok) {
        // Refresh groups list
        if (onDataRefresh) {
          onDataRefresh();
        }
        alert(data.message || "Grupo deletado com sucesso. Os cursos foram tornados públicos.");
      } else {
        alert(data.message || "Erro ao deletar grupo");
      }
    } catch (error) {
      console.error("Error force deleting group:", error);
      alert("Erro ao deletar grupo. Tente novamente.");
    }
  };

  const handleCreateGroup = async (groupData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...groupData, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowGroupForm(false);
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        throw new Error(data.message || "Erro ao criar grupo");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroup = async (groupId, groupData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/groups/${groupId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...groupData, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowGroupForm(false);
        setEditingGroup(null);
        // Refresh data
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        throw new Error(data.message || "Erro ao atualizar grupo");
      }
    } catch (error) {
      console.error("Error updating group:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGroupForm = (group = null) => {
    setEditingGroup(group);
    setShowGroupForm(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowGroupForm(true);
  };

  // CSV Import actions
  const handleCSVImport = async (users) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/users/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ users, user_id: user.id }),
      });

      const data = await response.json();
      if (response.ok) {
        setShowCSVImport(false);
        // Refresh users list
        // Note: Data will be refreshed automatically by useAdminData hook
      } else {
        throw new Error(data.message || "Erro ao importar usuários");
      }
    } catch (error) {
      console.error("Error importing CSV:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Export actions
  const exportUsersCSV = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/users/export?user_id=${user.id}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "usuarios.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error("Erro ao exportar CSV");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const exportCourseProgressCSV = async (courseFilter = "", userFilter = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        user_id: user.id
      });
      
      if (courseFilter) {
        params.append("course_filter", courseFilter);
      }
      if (userFilter) {
        params.append("user_filter", userFilter);
      }
      
      const response = await fetch(
        `${API_BASE_URL}/admin/course-progress/export?${params}`
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "progresso_cursos.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error("Erro ao exportar CSV");
      }
    } catch (error) {
      console.error("Error exporting course progress CSV:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Filter actions
  const handleProgressFilterChange = (field, value) => {
    setProgressFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearProgressFilters = () => {
    setProgressFilters({ course_filter: "", user_filter: "" });
  };

  return {
    // Modal states
    showCreateForm,
    setShowCreateForm,
    showUserForm,
    setShowUserForm,
    showGroupForm,
    setShowGroupForm,
    showCSVImport,
    setShowCSVImport,
    editingCourse,
    setEditingCourse,
    editingUser,
    setEditingUser,
    editingGroup,
    setEditingGroup,

    // Loading state
    loading,

    // Course actions
    handleCreateCourse,
    handleUpdateCourse,
    handleEditCourse,
    handleDeleteCourse,
    handleDeactivateCourse,
    handleActivateCourse,

    // User actions
    handleCreateUser,
    handleUpdateUser,
    handleEditUser,
    handleViewUserDashboard,

    // Group actions
    handleDeleteGroup,
    handleCreateGroup,
    handleUpdateGroup,
    handleOpenGroupForm,
    handleEditGroup,

    // CSV actions
    handleCSVImport,

    // Export actions
    exportUsersCSV,
    exportCourseProgressCSV,

    // Filter actions
    handleProgressFilterChange,
    clearProgressFilters,
  };
};

export default useAdminActions;
